import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import type { IconGlyph } from '@ipfabric/ui-components/Icon/IconGlyph';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { copyToClipboard } from '@ipfabric/ui-components/utils/copyToClipboard';
import { Button, type ButtonProps } from 'components/Button/Button';
import { useState } from 'react';
import type { FC, ReactElement, SyntheticEvent } from 'react';

export interface CopyToClipboardProps
  extends Omit<ButtonProps, 'type' | 'variant' | 'icon'> {
  text: string;
  type?: ButtonProps['type'];
  variant?: ButtonProps['variant'];
  icon?: IconGlyph | null;
  renderButton?: (onCopyClick: (e: SyntheticEvent) => void) => ReactElement;
}

export const CopyToClipboard: FC<CopyToClipboardProps> = ({
  text,
  type = 'button',
  variant = 'tertiary',
  icon = 'fa-copy',
  renderButton,
  ...buttonProps
}) => {
  const [copied, setCopied] = useState<boolean>(false);
  const handleCopyClick = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    copyToClipboard(text);
    setCopied(true);
  };

  const copiedMsg = (
    <div className='flex items-center'>
      <Icon glyph='fa-check' size='xs' className='mr-2 text-green-400' />
      Copied
    </div>
  );

  return (
    <Tooltip
      placement='top'
      content={copied ? copiedMsg : 'Copy to clipboard'}
      onHideTooltip={() => setCopied(false)}
    >
      {renderButton !== undefined ? (
        renderButton(handleCopyClick)
      ) : (
        <Button
          variant={variant}
          size='xxs'
          icon={icon ?? undefined}
          type={type}
          onClick={handleCopyClick}
          {...buttonProps}
        />
      )}
    </Tooltip>
  );
};
